import {
  setDebug,
  themeParams,
  initData,
  viewport,
  init as initSDK,
  mockTelegramEnv,
  type ThemeParams,
  retrieveLaunchParams,
  emitEvent,
  miniApp,
  swipeBehavior,
  backButton,
  on,
  postEvent,
} from "@tma.js/sdk-react";

/**
 * Initializes the application and configures its dependencies.
 */
export async function init(options: {
  debug: boolean;
  eruda: boolean;
  mockForMacOS: boolean;
}): Promise<void> {
  setDebug(options.debug);
  initSDK({
    themeParams: {
      accent_text_color: "#6ab2f2",
      bg_color: "#fff",
      button_color: "#5288c1",
      button_text_color: "#ffffff",
      bottom_bar_bg_color: "#ffffff",
      destructive_text_color: "#ec3942",
      header_bg_color: "#17212b",
      hint_color: "#708499",
      link_color: "#6ab3f3",
      secondary_bg_color: "#fff",
      section_bg_color: "#17212b",
      section_header_text_color: "#6ab3f3",
      subtitle_text_color: "#708499",
      text_color: "#f5f5f5",
    },
  });

  // Add Eruda if needed.
  options.eruda &&
    void import("eruda").then(({ default: eruda }) => {
      eruda.init();
      eruda.position({ x: window.innerWidth - 50, y: 0 });
    });

  // Telegram for macOS has a ton of bugs, including cases, when the client doesn't
  // even response to the "web_app_request_theme" method. It also generates an incorrect
  // event for the "web_app_request_safe_area" method.
  if (options.mockForMacOS) {
    let firstThemeSent = false;
    mockTelegramEnv({
      onEvent(event, next) {
        if (event.name === "web_app_request_theme") {
          let tp: ThemeParams = {};
          if (firstThemeSent) {
            tp = themeParams.state();
          } else {
            firstThemeSent = true;
            tp ||= retrieveLaunchParams().tgWebAppThemeParams;
          }
          return emitEvent("theme_changed", { theme_params: tp });
        }

        if (event.name === "web_app_request_safe_area") {
          return emitEvent("safe_area_changed", {
            left: 0,
            top: 0,
            right: 0,
            bottom: 0,
          });
        }

        next();
      },
    });
  }

  initData.restore();

  if (backButton.isSupported()) {
    backButton.mount();
    backButton.onClick(() => {});
  }

  if (swipeBehavior.mount.isAvailable()) {
    swipeBehavior.mount();
    swipeBehavior.disableVertical();
  }

  if (miniApp.mount.isAvailable()) {
    themeParams.mount();
    miniApp.mount();
    themeParams.bindCssVars();
    miniApp.bindCssVars();
  }

  if (viewport.mount.isAvailable()) {
    viewport.mount().then(() => {
      viewport.bindCssVars();
    });
  }

  // Launched from a chat button the app opens as a half-height sheet — expand
  // it to full height (not to be confused with requestFullscreen). A single
  // request sent during the opening animation is dropped by Telegram, so
  // besides the immediate call we re-send it whenever the viewport settles
  // (is_state_stable) still unexpanded. The window is limited to the launch
  // phase so we never fight the user dragging the sheet down later.
  const requestExpand = () => postEvent("web_app_expand");
  const launchedAt = performance.now();
  requestExpand();
  [300, 800, 1500].forEach((delay) => setTimeout(requestExpand, delay));
  on("viewport_changed", (payload) => {
    if (
      performance.now() - launchedAt < 5000 &&
      payload.is_state_stable &&
      !payload.is_expanded
    ) {
      requestExpand();
    }
  });

  miniApp.ready.ifAvailable();
}
